/* Copyright (c) 2019-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { DeviceOSType, CommonDeviceType } from '../../platform-detector/platform-detector';
import {
    UndefinedString,
    GfcEventsCommonData,
    UserConsent,
    ConsentLevel,
    ClientType,
    ClientVariant
} from './common.interfaces';

export const FeedbackProtocolVersion = '4.4';

export const GfcEventsDefaultCommonData: GfcEventsCommonData = {
    clientId: '{CLIENTID}',
    clientVer: '{CLIENTVER}',
    feedbackId: '{FEEDBACKID}',
    feedbackName: '{FEEDBACKNAME}',
    feedbackProtocol: FeedbackProtocolVersion,
    feedbackSchemaVer: '{FEEDBACKSCHEMAVER}',
    feedbackSysVer: '',
    deviceId: UndefinedString,
    userId: UndefinedString,
    externalUserId: UndefinedString,
    idpId: UndefinedString,
    sessionId: UndefinedString,
    sentTs: '',
    clientTs: '',
    deviceOS: DeviceOSType.Undefined,
    deviceOSVersion: UndefinedString,
    deviceMake: UndefinedString,
    deviceModel: UndefinedString,
    deviceType: CommonDeviceType.Undefined,
    clientType: ClientType.Undefined,
    clientVariant: ClientVariant.Undefined,
    browserType: UndefinedString,
    integrationId: UndefinedString,
    productName: UndefinedString,
    productVersion: UndefinedString,
    cpuArchitecture: UndefinedString
};

export const GdprConsentNone: Readonly<UserConsent> = {
    functional: ConsentLevel.None,
    technical: ConsentLevel.None,
    behavioral: ConsentLevel.None
};

export const ChunkConfig = {
    bufferSize: 5120, // 5Kb (in bytes)
    chunkCreationTimeout: 300000, // 5mins in ms
    chunksQueueSize: 20, // Max number of chunks in the chunks queue
    chunkResendRetryLimit: 2
};

export const Undefined = 'undefined';

export enum GfcHeadRequestStatusCodes {
    FeedbackRequestAccepted = 200, // Waiting for attachment data if any
    FeedbackSizeOverLimits = 413, // Feedback JSON is too big to push it into Kafka
    InvalidFeedback = 460, // Feedback is invalid per some criteria
    FeedbackIdInUse = 461, // Feedback ID is already in use.
    FeedbackIdExpired = 462, // FeedbackId is completed or expired
    AttachmentsRejected = 466, // status = ATTACHMENTS_REJECTED or status = ATTACHMENTS_TOO_LARGE
    UnknownClientId = 467, // Unknown/unconfigured client id
    ServerError = 500 // Server side fatal error condition
}

export enum GfcAttachmentRequestStatusCodes {
    AllFramesReceived = 200, // All frames are successfully received. Feedback Id is discarded. Session closed.
    PartialFramesReceived = 206, // Partial content.  Frame is accepted.  Waiting for the next frame.
    InvalidFeedbackId = 462, // FeedbackId is completed or expired
    NonSequentialChunk = 463, // Use X-Data-Offset header in the reply for further chunk synchronization
    FrameChecksumOrSizeMismatch = 464, // Wrong content. Reason: frame checksum and/or size mismatch. Feedback ID remains in the wait state.
    ServerError = 500 // Server side fatal error condition.
}
